import React, { useEffect, useMemo, useState } from "react";
import { formatMoney, getNumber } from "../../../features/utils/formatMoney";
import { getCurrencySymbol } from "../../../features/utils/countires";
import CommonCollapsible from "../../common/CommonCollapsible";
import { clone } from "../../../features/utils/objClone";
import {
  resolveRefundMarkupAddBack,
  reverseOriginalAdjustment,
} from "../../../utils/booking/refundQuote";

/** Lines Ops enter when quoting a refund (matches backend). */
export const BASE_BREAKDOWN = [
  { name: "Penalty", amount: 0, isDeducted: true, isSupplierFee: false },
  { name: "Supplier Fee", amount: 0, isDeducted: true, isSupplierFee: true },
  { name: "Base Return", amount: 0, isDeducted: false, isSupplierFee: false },
  { name: "Tax Return", amount: 0, isDeducted: false, isSupplierFee: false },
];

/** Markup only on return lines (not Penalty / Supplier Fee). */
const MARKUP_FIELD_LABELS = ["Base Return", "Tax Return"];

const defaultMarkup = {
  variant: "Markup",
  type: "Fixed",
  amount: "",
};

const defaultMarkupTargets = {
  "Base Return": true,
  "Tax Return": true,
};

const inputClass =
  "w-full h-8 px-2.5 text-sm border border-[#DBDBDB] rounded-lg bg-white text-[#0F172A] placeholder:text-[#A4B1C1] focus:outline-none focus:border-[#94A3B8]";

const selectClass =
  "h-8 px-2 text-xs border border-[#DBDBDB] rounded-lg bg-white text-[#0F172A] focus:outline-none focus:border-[#94A3B8]";

/**
 * Refund return lines only: Markup takes money OFF what the agent gets back
 * (you keep more). Markdown gives the agent more than the airline return.
 */
function applyRefundReturnAdjustment(base, markup) {
  const baseN = Math.max(0, Number(base) || 0);
  const raw = Number(markup?.amount);
  if (!Number.isFinite(raw) || raw <= 0) return baseN;

  const delta = markup?.type === "Percentage" ? baseN * (raw / 100) : raw;

  if (markup?.variant === "Markdown") {
    return baseN + delta;
  }
  // Markup → lower agent return
  return Math.max(0, baseN - delta);
}

/** @deprecated use applyRefundReturnAdjustment for refund returns */
function applyAdjustment(base, markup) {
  return applyRefundReturnAdjustment(base, markup);
}

/** Platform keep on a return line = airline return − agent return */
function platformKeepOnReturn(airline, agentGets) {
  return Number(airline || 0) - Number(agentGets || 0);
}

function fieldKey(name) {
  const n = name || "";
  if (/supplier fee/i.test(n)) return "Supplier Fee";
  if (/^penalty$/i.test(n) || /refund charge|refund fee/i.test(n)) {
    return "Penalty";
  }
  if (/base return|refundable value|refund amount/i.test(n)) {
    return "Base Return";
  }
  if (/tax return|^tax$/i.test(n) || /refundable tax/i.test(n)) {
    return "Tax Return";
  }
  // Auto line for backend: unit − Base − Tax (isDeducted: true)
  if (/^deductible$/i.test(n) || /non-?refundable (ticket|value|amount)?$/i.test(n)) {
    return "Deductible";
  }
  return n;
}

export function isComputedDeductibleLine(row) {
  return fieldKey(row?.name) === "Deductible";
}

/**
 * Backend refund ≈ unit − Σ(isDeducted).
 * Deductible = unit − Base Return − Tax Return so that
 * unit − Deductible − Penalty − Supplier Fee = Base + Tax − fees.
 */
function roundMoney(n) {
  return Math.round(Math.max(0, Number(n) || 0) * 100) / 100;
}

/**
 * Payload Deductible = original fare − Base − Tax
 * (original = sold unit after booking markup stripped — not the marked-up unit).
 */
export function buildDeductibleBreakdownRow({
  unitPrice,
  deductibleBasis,
  baseAirline = 0,
  taxAirline = 0,
  baseAgent = 0,
  taxAgent = 0,
}) {
  const basis = Math.max(
    0,
    Number(deductibleBasis != null ? deductibleBasis : unitPrice) || 0,
  );
  return {
    name: "Deductible",
    amount: roundMoney(basis - Number(baseAgent || 0) - Number(taxAgent || 0)),
    airlineAmount: roundMoney(
      basis - Number(baseAirline || 0) - Number(taxAirline || 0),
    ),
    isDeducted: true,
  };
}

/**
 * Infer markup from saved Base/Tax Return rows when backend does not store markup.
 * Same absolute (airline − amount) on both → Fixed; otherwise Percentage.
 * Only intended for a single passenger-type quote for now.
 */
export function inferMarkupFromBreakdown(breakDown = []) {
  const rows = Array.isArray(breakDown) ? breakDown : [];
  const pick = (label) =>
    rows.find((row) => fieldKey(row?.name) === label) || null;

  const lineMeta = (row) => {
    if (!row) return null;
    const airline = Number(row?.airlineAmount ?? 0);
    const agent = Number(row?.amount ?? 0);
    if (!Number.isFinite(airline) || !Number.isFinite(agent)) return null;
    if (airline <= 0 && agent <= 0) return null;
    const delta = airline - agent; // >0 Markup (take off), <0 Markdown
    if (Math.abs(delta) < 0.005) return null;
    return { airline, agent, delta };
  };

  const base = lineMeta(pick("Base Return"));
  const tax = lineMeta(pick("Tax Return"));
  const lines = [
    base ? { key: "Base Return", ...base } : null,
    tax ? { key: "Tax Return", ...tax } : null,
  ].filter(Boolean);

  const empty = {
    markup: { variant: "Markup", type: "Fixed", amount: "" },
    markupTargets: { ...defaultMarkupTargets },
  };
  if (!lines.length) return empty;

  const variant = lines.every((l) => l.delta > 0)
    ? "Markup"
    : lines.every((l) => l.delta < 0)
      ? "Markdown"
      : "Markup";

  const absDeltas = lines.map((l) => Math.abs(l.delta));
  const sameFixed =
    absDeltas.length >= 1 &&
    absDeltas.every((d) => Math.abs(d - absDeltas[0]) <= 0.5);

  let type;
  let amount;
  if (sameFixed && lines.length >= 2) {
    type = "Fixed";
    amount = String(Math.round(absDeltas[0] * 100) / 100);
  } else {
    // Different abs deltas (or a single line) → percentage of airline amount
    type = "Percentage";
    const pcts = lines
      .filter((l) => l.airline > 0)
      .map((l) => (Math.abs(l.delta) / l.airline) * 100);
    const avg = pcts.reduce((a, b) => a + b, 0) / Math.max(pcts.length, 1);
    amount = String(Math.round(avg * 100) / 100);
  }

  const markupTargets = {
    "Base Return": Boolean(base),
    "Tax Return": Boolean(tax),
  };

  return {
    markup: { variant, type, amount },
    markupTargets,
  };
}

function isReturnLine(row) {
  return row?.isDeducted === false;
}

function isFeeLine(row) {
  return !isReturnLine(row);
}

function airlineReturnsTotal(breakdown, overrideIndex, overrideAirline) {
  return (breakdown || []).reduce((acc, row, idx) => {
    if (!isReturnLine(row)) return acc;
    const airline =
      idx === overrideIndex
        ? Math.max(0, Number(overrideAirline) || 0)
        : Math.max(0, Number(row?.amount) || 0);
    return acc + airline;
  }, 0);
}

/** Agent-facing Base Return + Tax Return after markup. */
function sellingReturnsTotal(
  breakdown,
  markup,
  markupTargets,
  overrideIndex,
  overrideAirline,
) {
  return (breakdown || []).reduce((acc, row, idx) => {
    if (!isReturnLine(row)) return acc;
    const airline =
      idx === overrideIndex
        ? Math.max(0, Number(overrideAirline) || 0)
        : Math.max(0, Number(row?.amount) || 0);
    const key = fieldKey(row?.name);
    const applies = Boolean(markupTargets?.[key]);
    return (
      acc +
      (applies ? applyRefundReturnAdjustment(airline, markup) : airline)
    );
  }, 0);
}

/**
 * Max airline amount on a return line.
 * Cap is the original (pre–booking-markup) fare — not the sold/marked-up unit.
 * After quote markdown, agent Base+Tax also cannot exceed that same cap.
 */
function maxReturnAirlineForTicket(
  breakdown,
  index,
  markup,
  markupTargets,
  originalFareCap,
) {
  const cap = Math.max(0, Number(originalFareCap) || 0);
  const row = breakdown?.[index];
  if (!row || !isReturnLine(row)) return Number.POSITIVE_INFINITY;

  const otherAirline = airlineReturnsTotal(breakdown, index, 0);
  const airlineRoom = Math.max(0, cap - otherAirline);

  const applies = Boolean(markupTargets?.[fieldKey(row?.name)]);
  const raw = Number(markup?.amount);
  if (
    !applies ||
    !Number.isFinite(raw) ||
    raw <= 0 ||
    markup?.variant !== "Markdown"
  ) {
    return airlineRoom;
  }

  // Markdown raises agent amount → keep agent total ≤ original fare
  const otherSelling = sellingReturnsTotal(
    breakdown,
    markup,
    markupTargets,
    index,
    0,
  );
  const sellingRoom = Math.max(0, cap - otherSelling);
  if (markup?.type === "Percentage") {
    const factor = 1 + raw / 100;
    if (factor <= 0) return 0;
    return Math.min(airlineRoom, sellingRoom / factor);
  }
  return Math.min(airlineRoom, Math.max(0, sellingRoom - raw));
}

/**
 * Max quote markup/markdown amount.
 * Markup: lines stay ≥ 0.
 * Markdown: agent Base+Tax ≤ original fare cap.
 */
function maxMarkupAmountForReturns(
  breakdown,
  markupTargets,
  variant,
  type,
  originalFareCap,
) {
  const ticket = Math.max(0, Number(originalFareCap) || 0);

  let untargeted = 0;
  let targetedAirline = 0;
  let targetedCount = 0;
  let minTargetedAirline = Number.POSITIVE_INFINITY;
  for (const row of breakdown || []) {
    if (!isReturnLine(row)) continue;
    const airline = Math.max(0, Number(row?.amount) || 0);
    const applies = Boolean(markupTargets?.[fieldKey(row?.name)]);
    if (applies) {
      targetedAirline += airline;
      targetedCount += 1;
      minTargetedAirline = Math.min(minTargetedAirline, airline);
    } else {
      untargeted += airline;
    }
  }

  if (variant === "Markup") {
    if (type === "Percentage") return 100;
    if (targetedCount <= 0) return Number.POSITIVE_INFINITY;
    return Number.isFinite(minTargetedAirline) ? minTargetedAirline : 0;
  }

  // Markdown increases agent return
  const room = ticket - untargeted;
  if (room < 0) return 0;
  if (type === "Percentage") {
    if (targetedAirline <= 0) return Number.POSITIVE_INFINITY;
    return Math.max(0, (room / targetedAirline - 1) * 100);
  }
  if (targetedCount <= 0) return Number.POSITIVE_INFINITY;
  return Math.max(0, (room - targetedAirline) / targetedCount);
}

export default function PassengerBreakdownCard({
  normalized,
  serviceFee,
  passengerCount,
  item,
  travellerLabel,
  travelerType,
  handleSaveBreakdown,
  unitPrice = 0,
  unitBase = 0,
  unitTax = 0,
  /** Original booking appliedAdjustments row (Markup/Markdown). */
  bookingAdjustment = null,
  /** Backend Intraverse Markup after quote (null = FE estimate). */
  intraverseMarkup = null,
}) {
  const [open, setOpen] = useState(false);
  const [localBreakdown, setLocalBreakdown] = useState([]);
  const [markup, setMarkup] = useState(defaultMarkup);
  const [markupTargets, setMarkupTargets] = useState(defaultMarkupTargets);

  useEffect(() => {
    setLocalBreakdown(clone(item?.breakDown || []));
    setMarkup({
      variant: item?.markup?.variant || "Markup",
      type: item?.markup?.type || "Fixed",
      amount:
        item?.markup?.amount != null && item?.markup?.amount !== ""
          ? String(item.markup.amount)
          : "",
    });
    setMarkupTargets({
      ...defaultMarkupTargets,
      ...(item?.markupTargets || {}),
    });
    // eslint-disable-next-line
  }, [open, item?.travelerType]);

  const globalBreakdown = useMemo(() => item?.breakDown || [], [item]);

  const hasGlobalQuoteValue = useMemo(() => {
    return (globalBreakdown || []).some((x) => Number(x?.amount || 0) > 0);
  }, [globalBreakdown]);

  const adjustedBreakdown = useMemo(() => {
    return (localBreakdown || []).map((row) => {
      const key = fieldKey(row?.name);
      const applies = Boolean(markupTargets[key]) && isReturnLine(row);
      const airline = Math.max(0, Number(row?.amount) || 0);
      return {
        ...row,
        name: key,
        adjusted: applies
          ? applyRefundReturnAdjustment(airline, markup)
          : airline,
        markupApplies: applies,
      };
    });
  }, [localBreakdown, markup, markupTargets]);

  const isDirty = useMemo(() => {
    const g = globalBreakdown || [];
    const l = localBreakdown || [];
    if (g.length !== l.length) return true;
    for (let i = 0; i < g.length; i++) {
      if (Number(g[i]?.amount || 0) !== Number(l[i]?.amount || 0)) return true;
    }
    const gm = item?.markup || defaultMarkup;
    if ((gm.variant || "Markup") !== (markup.variant || "Markup")) return true;
    if ((gm.type || "Fixed") !== (markup.type || "Fixed")) return true;
    if (String(gm.amount || "") !== String(markup.amount || "")) return true;

    const gt = { ...defaultMarkupTargets, ...(item?.markupTargets || {}) };
    for (const key of MARKUP_FIELD_LABELS) {
      if (Boolean(gt[key]) !== Boolean(markupTargets[key])) return true;
    }
    return false;
  }, [
    globalBreakdown,
    localBreakdown,
    markup,
    markupTargets,
    item?.markup,
    item?.markupTargets,
  ]);

  const icon = useMemo(() => {
    switch (travelerType) {
      case "CHD":
      case "CNN":
        return "./flightChild.svg";
      case "INF":
      case "INS":
        return "./flightInfant.svg";
      default:
        return "./flightAdult.svg";
    }
  }, [travelerType]);

  const localReturnsPerPax = useMemo(() => {
    return adjustedBreakdown.reduce((acc, cur) => {
      if (!isReturnLine(cur)) return acc;
      return acc + Number(cur?.adjusted || 0);
    }, 0);
  }, [adjustedBreakdown]);

  /** What Intraverse keeps from return lines (airline return − agent gets). */
  const platformKeepPerPax = useMemo(() => {
    return adjustedBreakdown.reduce((acc, cur) => {
      if (!isReturnLine(cur)) return acc;
      return acc + platformKeepOnReturn(cur?.amount, cur?.adjusted);
    }, 0);
  }, [adjustedBreakdown]);

  const airlineReturnsPerPax = useMemo(() => {
    return adjustedBreakdown.reduce((acc, cur) => {
      if (!isReturnLine(cur)) return acc;
      return acc + Number(cur?.amount || 0);
    }, 0);
  }, [adjustedBreakdown]);

  // Strip % from base → original unit (deductible / Base+Tax cap).
  const bookingAdjMeta = useMemo(
    () => reverseOriginalAdjustment(unitPrice, bookingAdjustment, unitBase),
    [unitPrice, unitBase, bookingAdjustment],
  );
  const underlyingValue = bookingAdjMeta.underlyingValue;

  // Markup add-back: backend Intraverse Markup wins; else FE estimate.
  const markupAddBack = useMemo(
    () =>
      resolveRefundMarkupAddBack({
        breakDown: globalBreakdown,
        unitPrice,
        unitBase,
        adjustment: bookingAdjustment,
        intraverseMarkup,
      }),
    [
      globalBreakdown,
      unitPrice,
      unitBase,
      bookingAdjustment,
      intraverseMarkup,
    ],
  );
  const heldBookingAdj = markupAddBack.heldAdjustment;
  const bookingAdjType = markupAddBack.type; // Markup | Markdown | null
  const markupFromBackend = markupAddBack.source === "backend";

  // Base+Tax cap = original unit (after stripping base markup)
  const returnCap = Number(underlyingValue || unitPrice || 0);

  // Keep Base+Tax within original fare when cap / quote markup changes
  useEffect(() => {
    if (!open) return;
    setLocalBreakdown((prev) => {
      const next = clone(prev || []);
      let changed = false;
      for (let i = 0; i < next.length; i++) {
        if (!isReturnLine(next[i])) continue;
        const maxAllowed = maxReturnAirlineForTicket(
          next,
          i,
          markup,
          markupTargets,
          returnCap,
        );
        const cur = Math.max(0, Number(next[i].amount) || 0);
        if (Number.isFinite(maxAllowed) && cur > maxAllowed + 0.0001) {
          next[i].amount = Math.max(0, maxAllowed);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line
  }, [open, returnCap, markup.variant, markup.type, markup.amount, markupTargets]);

  const deductiblePerPax = useMemo(() => {
    const baseAirline = Number(
      adjustedBreakdown.find((r) => r?.name === "Base Return")?.amount || 0,
    );
    const taxAirline = Number(
      adjustedBreakdown.find((r) => r?.name === "Tax Return")?.amount || 0,
    );
    const baseAgent = Number(
      adjustedBreakdown.find((r) => r?.name === "Base Return")?.adjusted || 0,
    );
    const taxAgent = Number(
      adjustedBreakdown.find((r) => r?.name === "Tax Return")?.adjusted || 0,
    );
    // Payload deductible = original unit − Base − Tax
    const row = buildDeductibleBreakdownRow({
      unitPrice,
      deductibleBasis: returnCap,
      baseAirline,
      taxAirline,
      baseAgent,
      taxAgent,
    });
    const opsFeesAgent = adjustedBreakdown.reduce((sum, r) => {
      if (isReturnLine(r)) return sum;
      return sum + Number(r?.adjusted || 0);
    }, 0);
    const opsFeesAirline = adjustedBreakdown.reduce((sum, r) => {
      if (isReturnLine(r)) return sum;
      return sum + Number(r?.amount || 0);
    }, 0);
    return {
      ...row,
      fieldAmount: row.amount,
      fieldAirlineAmount: row.airlineAmount,
      totalAmount: Math.round((row.amount + opsFeesAgent) * 100) / 100,
      totalAirlineAmount:
        Math.round((row.airlineAmount + opsFeesAirline) * 100) / 100,
      opsFeesAirline,
      opsFeesAgent,
    };
  }, [adjustedBreakdown, unitPrice, returnCap]);

  /**
   * Strip base markup → returns − fees → add held markup back.
   * Cap final at sold unit.
   */
  const ladder = useMemo(() => {
    const airlineReturns = airlineReturnsPerPax;
    const agentReturns = localReturnsPerPax;
    const airlineFees = Number(deductiblePerPax.opsFeesAirline || 0);
    const agentFees = Number(deductiblePerPax.opsFeesAgent || 0);
    const cap = Number(unitPrice || 0);

    const applyHeld = (returns, fees) => {
      let net =
        Math.round((Number(returns || 0) - Number(fees || 0)) * 100) / 100;
      if (bookingAdjType === "Markdown") {
        net = Math.round((net - heldBookingAdj) * 100) / 100;
      } else if (bookingAdjType === "Markup") {
        net = Math.round((net + heldBookingAdj) * 100) / 100;
      }
      return Math.max(0, Math.min(cap, net));
    };

    const airlineFinal = applyHeld(airlineReturns, airlineFees);
    const agentFinal = applyHeld(agentReturns, agentFees);

    return {
      airlineDeductable: Number(deductiblePerPax.totalAirlineAmount || 0),
      agentDeductable: Number(deductiblePerPax.totalAmount || 0),
      airlineSide: airlineReturns,
      agentSide: agentReturns,
      airlineFees,
      agentFees,
      airlineFinal,
      agentFinal,
      quoteMarkupEffect: Math.round((airlineFinal - agentFinal) * 100) / 100,
    };
  }, [
    unitPrice,
    heldBookingAdj,
    bookingAdjType,
    airlineReturnsPerPax,
    localReturnsPerPax,
    deductiblePerPax.opsFeesAirline,
    deductiblePerPax.opsFeesAgent,
    deductiblePerPax.totalAirlineAmount,
    deductiblePerPax.totalAmount,
  ]);

  const refundPerPax = ladder.agentFinal;
  const airlineRefundPerPax = ladder.airlineSide;

  const refundTotal = useMemo(() => {
    return refundPerPax * Number(passengerCount || 0);
  }, [refundPerPax, passengerCount]);

  const exceedsOriginalFare = useMemo(() => {
    const cap = Number(returnCap || 0);
    return (
      Number(airlineReturnsPerPax || 0) - cap > 0.0001 ||
      Number(localReturnsPerPax || 0) - cap > 0.0001
    );
  }, [airlineReturnsPerPax, localReturnsPerPax, returnCap]);

  const onChangeLocal = (i, v) => {
    const next = clone(localBreakdown || []);
    if (!next?.[i]) return;

    let val = Math.max(0, Number(getNumber(v) || 0));

    // Base/Tax ≤ original unit (after stripping base markup)
    if (isReturnLine(next[i])) {
      const maxAllowed = maxReturnAirlineForTicket(
        next,
        i,
        markup,
        markupTargets,
        returnCap,
      );
      if (Number.isFinite(maxAllowed)) {
        val = Math.min(val, maxAllowed);
      }
    }

    next[i].amount = val;
    setLocalBreakdown(next);
  };

  const clampMarkupAmount = (nextMarkup, targets = markupTargets) => {
    const next = { ...nextMarkup };
    if (
      next.variant === "Markdown" &&
      next.type === "Percentage" &&
      Number(next.amount) > 100
    ) {
      next.amount = "100";
    }
    if (next.amount !== "" && next.amount != null) {
      const maxAmt = maxMarkupAmountForReturns(
        localBreakdown,
        targets,
        next.variant || "Markup",
        next.type || "Fixed",
        returnCap,
      );
      if (Number.isFinite(maxAmt) && Number(next.amount) > maxAmt) {
        next.amount = String(Math.max(0, Math.floor(maxAmt * 100) / 100));
      }
    }
    return next;
  };

  const onMarkupChange = (patch) => {
    setMarkup((prev) => clampMarkupAmount({ ...prev, ...patch }));
  };

  const toggleMarkupTarget = (label) => {
    const nextTargets = {
      ...markupTargets,
      [label]: !markupTargets[label],
    };
    setMarkupTargets(nextTargets);
    setMarkup((m) => clampMarkupAmount(m, nextTargets));
  };

  const hasQuoteMarkup =
    Number(markup?.amount) > 0 &&
    (Boolean(markupTargets["Base Return"]) ||
      Boolean(markupTargets["Tax Return"]));

  const onSave = () => {
    if (!isDirty || exceedsOriginalFare) {
      if (!isDirty) setOpen(false);
      return;
    }
    handleSaveBreakdown?.(
      item?.travelerType,
      localBreakdown,
      {
        ...markup,
        amount: markup.amount === "" ? 0 : Number(markup.amount),
      },
      markupTargets,
    );
    setOpen(false);
  };

  const bookingPctLabel =
    bookingAdjMeta.method === "Percentage" &&
    bookingAdjustment?.amount != null
      ? `${bookingAdjustment.amount}%`
      : null;

  return (
    <div className="rounded-xl bg-white border border-[#E8EEF5] flex flex-col">
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer"
        onClick={() => setOpen((s) => !s)}
      >
        <div className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
          <img
            src={icon}
            alt=""
            className={
              "w-5 h-5 object-contain " +
              (icon === "./flightChild.svg" ? "mb-0.5" : "")
            }
          />
          <span>{travellerLabel?.[normalized] || normalized}</span>
          <span className="text-xs text-[#6D7C94] font-normal">
            ×{passengerCount}
          </span>
        </div>

        {!hasGlobalQuoteValue ? (
          <button
            type="button"
            className="h-7 rounded-lg border border-[#CBD5E1] text-[#0F172A] px-2.5 text-xs font-semibold bg-white hover:bg-[#F8FAFC]"
          >
            Add Quote
          </button>
        ) : (
          <div className="text-sm font-semibold text-[#0F172A]">
            {formatMoney(refundTotal)}
          </div>
        )}
      </div>

      <CommonCollapsible open={open}>
        <div className="border-t border-[#E8EEF5] px-3 pb-3 pt-2 space-y-3">
          {/* Cap context — one line */}
          <div className="rounded-lg bg-[#F8FAFC] border border-[#E8EEF5] px-2.5 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-[#64748B]">
                Original unit{" "}
                <span className="text-[10px] text-[#94A3B8]">
                  (Base + Tax max)
                </span>
              </span>
              <span className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(returnCap)}
              </span>
            </div>
            {bookingAdjType && heldBookingAdj > 0 ? (
              <div className="text-[10px] text-[#94A3B8] mt-0.5">
                {markupFromBackend ? (
                  <>
                    Intraverse Markup from quote {formatMoney(heldBookingAdj)}{" "}
                    (used in estimate)
                  </>
                ) : (
                  <>
                    Sold {formatMoney(unitPrice)} · {bookingPctLabel || ""} base{" "}
                    {bookingAdjType.toLowerCase()} ~{formatMoney(heldBookingAdj)}{" "}
                    (estimate until quote returns Intraverse Markup)
                  </>
                )}
              </div>
            ) : null}
          </div>

          {/* Inputs */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                Airline amounts
              </span>
              {hasQuoteMarkup ? (
                <span className="text-[10px] text-[#94A3B8]">
                  Agent after quote adj
                </span>
              ) : null}
            </div>
            {adjustedBreakdown.map((obj, i) => {
              const keep = platformKeepOnReturn(obj?.amount, obj?.adjusted);
              const credit = isReturnLine(obj);
              const soldUnitHint =
                obj?.name === "Base Return" && Number(unitBase || 0) > 0
                  ? `Sold unit base ${formatMoney(unitBase)}`
                  : obj?.name === "Tax Return" && Number(unitTax || 0) > 0
                    ? `Sold unit tax ${formatMoney(unitTax)}`
                    : null;
              return (
                <div
                  key={obj?.name || i}
                  className="grid grid-cols-[minmax(0,1fr)_120px] gap-2 items-center"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0F172A] leading-tight">
                      {obj?.name}
                    </div>
                    {!credit ? (
                      <div className="text-[10px] text-[#94A3B8]">
                        Deducted from refund
                      </div>
                    ) : (
                      <>
                        {soldUnitHint ? (
                          <div className="text-[10px] text-[#94A3B8]">
                            {soldUnitHint}
                          </div>
                        ) : null}
                        {hasQuoteMarkup && keep !== 0 ? (
                          <div
                            className={`text-[10px] ${
                              keep > 0 ? "text-emerald-700" : "text-amber-700"
                            }`}
                          >
                            {keep > 0
                              ? `Keep ${formatMoney(keep)}`
                              : `Extra ${formatMoney(Math.abs(keep))}`}
                            {" · "}
                            agent {formatMoney(obj?.adjusted || 0)}
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${inputClass} text-right`}
                    placeholder="0"
                    value={
                      obj?.amount
                        ? String(Number(obj.amount).toLocaleString())
                        : ""
                    }
                    onChange={(e) => onChangeLocal(i, e.target.value)}
                  />
                </div>
              );
            })}
            {exceedsOriginalFare ? (
              <div className="text-[11px] text-[#B91C1C]">
                Base + Tax cannot exceed original unit (
                {formatMoney(returnCap)}).
              </div>
            ) : null}
          </div>

          {/* Quote markup — compact */}
          <div className="rounded-lg border border-[#E8EEF5] px-2.5 py-2 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-[#0F172A]">
                Quote markup on returns
              </span>
              {platformKeepPerPax !== 0 ? (
                <span
                  className={`text-[10px] font-semibold ${
                    platformKeepPerPax > 0
                      ? "text-emerald-700"
                      : "text-amber-700"
                  }`}
                >
                  {platformKeepPerPax > 0
                    ? `Keep ${formatMoney(platformKeepPerPax)}`
                    : `Extra ${formatMoney(Math.abs(platformKeepPerPax))}`}
                </span>
              ) : (
                <span className="text-[10px] text-[#94A3B8]">Optional</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {MARKUP_FIELD_LABELS.map((label) => {
                const on = Boolean(markupTargets[label]);
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleMarkupTarget(label)}
                    className={`h-6 rounded-md px-2 text-[10px] font-semibold border ${
                      on
                        ? "bg-primary1 text-white border-primary1"
                        : "bg-white text-[#64748B] border-[#E2E8F0]"
                    }`}
                  >
                    {label.replace(" Return", "")}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1.5">
              <select
                className={selectClass}
                value={markup.variant}
                onChange={(e) =>
                  onMarkupChange({ variant: e.target.value, amount: "" })
                }
              >
                <option value="Markup">Markup</option>
                <option value="Markdown">Markdown</option>
              </select>
              <select
                className={selectClass}
                value={markup.type}
                onChange={(e) =>
                  onMarkupChange({ type: e.target.value, amount: "" })
                }
              >
                <option value="Fixed">Fixed</option>
                <option value="Percentage">%</option>
              </select>
              <input
                type="text"
                inputMode="decimal"
                className={`${inputClass} flex-1`}
                placeholder={
                  markup.type === "Fixed" ? getCurrencySymbol("NGN") : "%"
                }
                value={markup.amount}
                onChange={(e) => {
                  const digitsOnly = String(e.target.value || "").replace(
                    /[^\d.]/g,
                    "",
                  );
                  onMarkupChange({ amount: digitsOnly });
                }}
              />
            </div>
          </div>

          {/* Estimate — short ladder */}
          <div className="rounded-lg border border-[#E8EEF5] px-2.5 py-2 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Estimate
            </div>
            {[
              {
                label: "Deductible (sent)",
                value: formatMoney(deductiblePerPax.fieldAirlineAmount),
                valueClass: "text-rose-700",
              },
              {
                label: "Airline refund (Base + Tax)",
                value: formatMoney(airlineRefundPerPax),
              },
              Number(ladder.airlineFees || 0) > 0.0001
                ? {
                    label: "Penalty / supplier fee",
                    value: `−${formatMoney(ladder.airlineFees)}`,
                    valueClass: "text-rose-700",
                  }
                : null,
              bookingAdjType === "Markup" && heldBookingAdj > 0
                ? {
                    label: markupFromBackend
                      ? "Intraverse Markup"
                      : `Booking markup estimate${
                          bookingPctLabel ? ` (${bookingPctLabel})` : ""
                        }`,
                    value: `+${formatMoney(heldBookingAdj)}`,
                    valueClass: "text-emerald-700",
                  }
                : null,
              bookingAdjType === "Markdown" && heldBookingAdj > 0
                ? {
                    label: markupFromBackend
                      ? "Intraverse Markup"
                      : "Booking markdown estimate",
                    value: `−${formatMoney(heldBookingAdj)}`,
                    valueClass: "text-rose-700",
                  }
                : null,
              Math.abs(ladder.quoteMarkupEffect) > 0.0001
                ? {
                    label: "Quote adj",
                    value: `${
                      ladder.quoteMarkupEffect > 0 ? "−" : "+"
                    }${formatMoney(Math.abs(ladder.quoteMarkupEffect))}`,
                    valueClass:
                      ladder.quoteMarkupEffect > 0
                        ? "text-emerald-700"
                        : "text-amber-700",
                  }
                : null,
            ]
              .filter(Boolean)
              .map((row) => (
                <div
                  key={row.label}
                  className="flex items-baseline gap-2 text-xs text-[#64748B]"
                >
                  <span className="shrink-0">{row.label}</span>
                  <span
                    className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[8px]"
                    aria-hidden
                  />
                  <span
                    className={`font-medium tabular-nums shrink-0 ${
                      row.valueClass || "text-[#0F172A]"
                    }`}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            <div className="flex items-baseline gap-2 border-t border-[#E8EEF5] pt-1.5">
              <span className="text-sm font-semibold text-[#0F172A] shrink-0">
                Refund / pax
              </span>
              <span
                className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[8px]"
                aria-hidden
              />
              <span className="text-sm font-semibold tabular-nums text-[#0F172A] shrink-0">
                {formatMoney(refundPerPax)}
              </span>
            </div>
            <div className="flex items-baseline gap-2 text-xs text-[#64748B]">
              <span className="shrink-0">×{passengerCount} passengers</span>
              <span
                className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[8px]"
                aria-hidden
              />
              <span className="font-semibold tabular-nums text-[#0F172A] shrink-0">
                {formatMoney(refundTotal)}
              </span>
            </div>
            {Number(serviceFee || 0) > 0 ? (
              <div className="text-[10px] text-[#92400E] pt-0.5">
                Service fee{" "}
                {formatMoney(
                  Number(serviceFee || 0) * Number(passengerCount || 1),
                )}{" "}
                (config · not in estimate)
              </div>
            ) : null}
            <div className="text-[10px] text-[#94A3B8] pt-0.5">
              Deductible = original fare {formatMoney(returnCap)} − Base − Tax
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!isDirty || exceedsOriginalFare}
              className={`h-8 rounded-lg px-4 text-xs font-semibold transition
                ${
                  isDirty && !exceedsOriginalFare
                    ? "bg-primary1 text-white hover:opacity-90"
                    : "bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed"
                }`}
            >
              Save
            </button>
          </div>
        </div>
      </CommonCollapsible>
    </div>
  );
}

export {
  MARKUP_FIELD_LABELS as FIELD_LABELS,
  applyAdjustment,
  applyRefundReturnAdjustment,
  defaultMarkupTargets,
  fieldKey,
  sellingReturnsTotal,
  maxReturnAirlineForTicket,
  maxMarkupAmountForReturns,
  isReturnLine,
  isFeeLine,
};
